import * as rxjs from 'rxjs';
import * as Elevated from '@pitaman71/omniglot-introspect';

import Dependencies from '../Dependencies';
import * as Mutations from '../Mutations';
import * as Properties from '../Properties';
import * as Stores from '../Stores';
import * as Streams from '../Streams';

const __moduleName__ = 'omniglot-live-data.Clients.ScalarClient';

export interface State<ValueType> { 
    value: undefined|ValueType, 
    status: Streams.Status,
    client?: ScalarClient<ValueType>
};

export abstract class ScalarClient<ValueType> {
    abstract assign(newValue: ValueType): this;
    abstract clear(): this;

    static debugMode = false;

    static fromActions<ValueType, DomainType extends Elevated.Domain<ValueType>>(
        name: string,
        stream: Streams.ScalarStream<ValueType, DomainType>,
        on: {
            assign(newValue: ValueType): void,
            clear(): void
        }
    ) {
        return stream.watch(name).mutations({ 
            next: (mutation_) => {
                if(mutation_.clear) on.clear();
                else if(mutation_.assign) {
                    if(mutation_.assign.value === undefined)
                        on.clear();
                    else
                        on.assign(mutation_.assign.value);
                }
            },
            error: () => {},
            complete: () => {}
        })
    }

    static fromDelta<ValueType, DomainType extends Elevated.Domain<ValueType>>(
        name: string,
        setValue: (delta: (_values: ValueType|undefined) => ValueType|undefined) => void,
        setStatus: (delta: (_status: Streams.Status) => Streams.Status) => void
    ) {
        const __className__ = `${__moduleName__}.fromDelta`;
        return new class extends ScalarClient<ValueType> {
            subscriptions = new Streams.Subscriptions();
            value: ValueType|undefined;

            _watch(stream: Streams.ScalarStream<ValueType, DomainType>) {
                this.subscriptions.unsubscribe();
                this.subscriptions = stream.watch(name).mutations({
                    next: (mutation) => {
                        if(ScalarClient.debugMode)
                            console.log(`ScalarClient(${name}) receives ${mutation}`);
                        if(mutation.revert) {
                            setValue(_values => undefined);
                            this._watch(stream);
                        } else if(mutation.status) {
                            const status_ = mutation.status.status;
                            if(setStatus) setStatus(_status => status_);
                        } else {
                            mutation.dispatch(this); 
                        }
                    }, 
                    complete: () => {},
                    error: (error: any) => {
                        console.error(`${__className__} subscription to ${stream.persistKey} receives error ${error}`)
                    }
                });
                return this.subscriptions;
            }

            watchParent(stream: Streams.ScalarStream<ValueType, DomainType>): Streams.Subscriptions {
                return this._watch(stream);
            }
            assign(newValue: ValueType) {
                this.value = newValue;
                setValue(_values => newValue);
                return this;
            }
            clear() {
                this.value = undefined;
                setValue(_values => undefined);
                return this;
            }
        }();
    }
    static Eager<ValueType>(name_: string) { 
        return new class _Variable {
            _name: string;
            _current: State<ValueType>;
            _downstream: rxjs.Subject<State<ValueType>>;
            _client?: ScalarClient<ValueType>;

            constructor(name: string) {
                this._name = name;
                this._current = { value: undefined, status: {} };
                this._downstream = Dependencies.makeSubject();
                this._client = undefined;
            }

            value() { return this._current.value; }
            status() { return this._current.status; }
            authorize(status: boolean) {
                this._current.status.authorized = status;
                if(!status) this.set(undefined);
            }
            set(value_: undefined|ValueType, status_?: Streams.Status) { 
                if(ScalarClient.debugMode)
                    console.log(`Eager CHANGE ${this._name} ${this._current.value} => ${value_}`);
                this._current.value = value_;
                if(status_ !== undefined) this._current.status = status_;
                this._downstream.next({...this._current});
            }
            error(err: any) {
                this._downstream.error(err);
            }
            complete() {
                this._downstream.complete();
            }
            upstream(
                stream_: Streams.ScalarStream<ValueType>
            ): _Variable {
                const client = ScalarClient.fromDelta<ValueType, Elevated.Domain<ValueType>>(this._name, delta => {
                    const value_ = delta(this._current.value)
                    this.set(value_);
                }, delta => {
                    const status_ = delta(this._current.status);
                    this._current.status = status_;
                });
                const subscription = client.watchParent(stream_);
                this._client = stream_.stateful();
                return this;
            }
            watch(observer: rxjs.Observer<State<ValueType>>) {
                return this._downstream.subscribe(observer);
            }
            change(): undefined|ScalarClient<ValueType> {
                return this._client;
            }
            stream(domain: Elevated.Domain<ValueType>, onPull?: () => Promise<any>) {
                const eager = this;
                return new class implements Streams.ScalarStream<ValueType>, Streams.Pullable {
                    persistKey = Stores.Key.fromEmpty();
                    peerId: string = Elevated.factories.makeObjectId();
                    domain = domain;
                    watch(peerId: string) { 
                        return new class {
                            mutations(observer: rxjs.Observer<Mutations.OfScalar.Mutation<ValueType>>) { 
                                let prev: null|Mutations.OfScalar.Mutation<ValueType> = null;
                                const send = (value_: undefined|ValueType) => {
                                    if(value_ === undefined) {
                                        const mutation_ = Mutations.OfScalar.Mutation.fromClear(prev);
                                        observer.next(mutation_);
                                        prev = mutation_;
                                    } else {
                                        const mutation_ = Mutations.OfScalar.Mutation.fromAssign(prev, value_);
                                        observer.next(mutation_);
                                        prev = mutation_;
                                    }
                                };
                                send(eager.value());
                                const subscriptions = new Streams.Subscriptions();
                                subscriptions.add(eager.watch({
                                    next: state_ => send(state_.value),
                                    error: err => {},
                                    complete: () => {}
                                }));
                                return subscriptions;
                            }
                        }
                    }
                    stateful() { return undefined }
                    persistent() { return undefined }
                    pull() { return !onPull ? Promise.resolve() : onPull() }
                }();        
            }
        }(name_);
    }
}
